import React, { useState, useRef, useCallback, useEffect } from "react";

interface VoiceInputProps {
  onTranscript: (text: string) => void;
  disabled?: boolean;
}

export default function VoiceInput({ onTranscript, disabled }: VoiceInputProps) {
  const speechSupported =
    typeof window !== "undefined" &&
    Boolean((window as any).SpeechRecognition || (window as any).webkitSpeechRecognition);
  const [recording, setRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const recognitionRef = useRef<any>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animFrameRef = useRef<number>(0);
  const [bars, setBars] = useState<number[]>([0, 0, 0, 0]);

  const startRecording = useCallback(async () => {
    if (disabled || recording || !speechSupported) return;

    try {
      const RecognitionCtor =
        (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
      if (!RecognitionCtor) return;
      const recognition = new RecognitionCtor();
      recognition.continuous = false;
      recognition.interimResults = true;
      recognition.lang = "en-US";

      recognition.onresult = (event: any) => {
        const transcript = Array.from(event.results)
          .map((result: any) => result[0].transcript)
          .join("");

        if (event.results[event.results.length - 1].isFinal) {
          setProcessing(false);
          setRecording(false);
          onTranscript(transcript);
          stopVisualization();
        }
      };

      recognition.onerror = () => {
        setRecording(false);
        setProcessing(false);
        stopVisualization();
      };

      recognition.onend = () => {
        setRecording(false);
        setProcessing(false);
        stopVisualization();
      };

      recognitionRef.current = recognition;
      recognition.start();
      setRecording(true);

      // Start audio visualization
      startVisualization();
    } catch {
      setRecording(false);
    }
  }, [disabled, recording, onTranscript, speechSupported]);

  const stopRecording = useCallback(() => {
    if (recognitionRef.current) {
      setProcessing(true);
      recognitionRef.current.stop();
    }
    stopVisualization();
  }, []);

  const startVisualization = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const ctx = new AudioContext();
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 32;
      source.connect(analyser);
      audioContextRef.current = ctx;
      analyserRef.current = analyser;

      const updateBars = () => {
        if (!analyserRef.current) return;
        const data = new Uint8Array(analyserRef.current.frequencyBinCount);
        analyserRef.current.getByteFrequencyData(data);
        // Sample 4 frequency bands
        setBars([data[1] / 255, data[3] / 255, data[5] / 255, data[7] / 255]);
        animFrameRef.current = requestAnimationFrame(updateBars);
      };
      updateBars();
    } catch {
      // Visualization optional — mic permission might not include audio analysis
    }
  };

  const stopVisualization = () => {
    cancelAnimationFrame(animFrameRef.current);
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    analyserRef.current = null;
    setBars([0, 0, 0, 0]);
  };

  useEffect(() => {
    return () => {
      stopVisualization();
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
    };
  }, []);

  if (recording || processing) {
    return (
      <button
        onClick={stopRecording}
        className="relative p-1.5 rounded-lg transition-colors"
        title="Stop recording"
      >
        <span className="voice-pulse-ring" />
        <div className="flex items-end gap-0.5 h-4">
          {bars.map((level, i) => (
            <span
              key={i}
              className="w-0.5 rounded-full transition-all duration-75"
              style={{
                backgroundColor: "var(--c-danger)",
                height: `${Math.max(4, level * 16)}px`,
              }}
            />
          ))}
        </div>
      </button>
    );
  }

  return (
    <button
      onClick={startRecording}
      disabled={disabled || !speechSupported}
      className="p-1.5 rounded-lg transition-colors disabled:opacity-30"
      style={{ color: "var(--c-text-faint)" }}
      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--c-hover)")}
      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "")}
      title={speechSupported ? "Voice input" : "Voice input unavailable in this browser"}
    >
      <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      >
        <rect x="5.5" y="1" width="5" height="8" rx="2.5" />
        <path d="M3 7a5 5 0 0010 0" />
        <path d="M8 12v3" />
        <path d="M5.5 15h5" />
      </svg>
    </button>
  );
}
